import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, FormControl, FormTextField } from 'components/common';
import { applicationFormApi } from 'api/applicationForm';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useNavigateBack } from 'hooks';
import { useIsIntroducerSectionEligible } from 'hooks/useIsIntroducerSectionEligible/useIsIntroducerSectionEligible';
import { getCurrentRoute } from 'utils/location';
import { EMAIL_REGEXP } from '../../../common/regexp';
import { isValidGbPhoneNumber } from 'utils/gbPhoneNumber';
import { ApplicationFormPageEnum } from '../types';
import { parseApplicationInProgress } from '../helpers';
import { buildHasIntroducerAnswer, parseIntroducerFormFromSavedAnswer } from './helpers';
import { introducerQuestions } from './questions';
import { IHasIntroducerAnswer, IIntroducerForm } from './types';

export const Introducer = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { isEligible, isLoading: isEligibilityLoading } = useIsIntroducerSectionEligible();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IIntroducerForm>({
    defaultValues: {
      hasIntroducer: null,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
    },
  });

  const formFields = form.watch();

  useEffect(() => {
    if (!isEligibilityLoading && !isEligible) {
      searchParams.set('section', ApplicationFormPageEnum.INTERMEDIARY_FEES);
      setSearchParams(searchParams);
    }
  }, [isEligible, isEligibilityLoading, searchParams, setSearchParams]);

  useEffect(() => {
    if (formFields.hasIntroducer !== 'true') {
      form.setValue('firstName', '');
      form.setValue('lastName', '');
      form.setValue('email', '');
      form.setValue('phoneNumber', '');
    }
  }, [formFields.hasIntroducer, form]);

  useEffect(() => {
    if (!applicationInProgress?.[currentRoute]) {
      return;
    }

    const parsed = parseApplicationInProgress(applicationInProgress[currentRoute]);
    const savedAnswer = parsed.hasIntroducer as IHasIntroducerAnswer | undefined;

    form.reset({
      hasIntroducer: null,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      ...parseIntroducerFormFromSavedAnswer(savedAnswer),
    });
  }, [applicationInProgress, currentRoute, form]);

  const submitFormHandler = async (formData: IIntroducerForm) => {
    const questions = [
      {
        question: intl.formatMessage({ id: introducerQuestions.hasIntroducer.labelKey }),
        key: 'hasIntroducer',
        answer: buildHasIntroducerAnswer(formData),
      },
    ];

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    searchParams.set('section', ApplicationFormPageEnum.INTERMEDIARY_FEES);
    setSearchParams(searchParams);
  };

  const isIntroducerDetailsIncomplete =
    formFields.hasIntroducer === 'true' &&
    (!formFields.firstName.trim() ||
      !formFields.lastName.trim() ||
      !formFields.email.trim() ||
      !formFields.phoneNumber.trim());

  const isNextDisabled = Boolean(!formFields.hasIntroducer) || isIntroducerDetailsIncomplete;

  if (isEligibilityLoading || !isEligible) {
    return null;
  }

  const isIntroducerSelected = formFields.hasIntroducer === 'true';

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <div className='mt-5'>
          <FormattedMessage id={introducerQuestions.hasIntroducer.labelKey} tagName={'h4'} />
          <div>
            <FormControl
              name={'hasIntroducer'}
              radioOptions={introducerQuestions.hasIntroducer.options}
              radioGroupProps={{ className: 'mt-2' }}
              onChangeEffect={(event: React.ChangeEvent<HTMLInputElement>) => {
                form.setValue('hasIntroducer', event.target.value);
              }}
            />
          </div>
        </div>

        {isIntroducerSelected && (
          <div className='mt-5 grid gap-5'>
            <section>
              <FormattedMessage id={introducerQuestions.firstName.labelKey} tagName={'h4'} />
              <FormTextField
                name={'firstName'}
                rules={{
                  required: requiredFieldErrorMessage,
                }}
              />
            </section>

            <section>
              <FormattedMessage id={introducerQuestions.lastName.labelKey} tagName={'h4'} />
              <FormTextField
                name={'lastName'}
                rules={{
                  required: requiredFieldErrorMessage,
                }}
              />
            </section>

            <section>
              <FormattedMessage id={introducerQuestions.email.labelKey} tagName={'h4'} />
              <FormTextField
                name={'email'}
                rules={{
                  required: requiredFieldErrorMessage,
                  pattern: {
                    value: EMAIL_REGEXP,
                    message: intl.formatMessage({ id: 'Must be a valid email address' }),
                  },
                }}
              />
            </section>

            <section>
              <FormattedMessage id={introducerQuestions.phoneNumber.labelKey} tagName={'h4'} />
              <FormTextField
                name={'phoneNumber'}
                rules={{
                  required: requiredFieldErrorMessage,
                  validate: value => {
                    const trimmed = String(value ?? '').trim();
                    if (!trimmed) {
                      return true;
                    }
                    return isValidGbPhoneNumber(trimmed) || intl.formatMessage({ id: 'common.error.validPhoneNumber' });
                  },
                }}
              />
            </section>
          </div>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'} disabled={isNextDisabled}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
